import mapValues from 'lodash/mapValues'
import uniqBy from 'lodash/uniqBy'

import { createMap } from '@app/utils/createMap'
import { createSelector } from '@app/utils/createSelector'
import { isTruthy } from '@app/utils/isTruthy'
import { normalize } from '@app/utils/normalizer'

import type { ApiAcademyLesson } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { academyEventDriverSelector } from './event'
import type { AcademyLesson } from './lib'
import { toCourseBundle } from './lib'

// Slices are declared before the selectors that reference them to avoid a TDZ error
// (createSelector reads slice.selector at module evaluation time).
const lessonModelsSlice = createReduxSlice<{ [id: string]: AcademyLesson }>('academy_lesson_models')

const lessonsListSlice = createReduxSlice<SliceState<string[]>>('academy_lessons_list')

// Merge lessons into the shared models map (e.g. cart items carry full lesson data).
export const addLessonModels = (lessons: AcademyLesson[]) => lessonModelsSlice.update(state => ({ ...state, ...createMap(lessons, l => l.id) }))

/** Both lesson reads carry the course, the sitter and their avatars; the models are built here. */
export const fetchAcademyLessons = (event?: Parameters<typeof import('@app/packages/api/v2/actions/academy').getAcademyLessons>[0], course_id?: string) =>
  createThunk(async dispatch => {
    const [{ getAcademyLessons }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/academy'), import('@app/store/actions/api')])
    const resp = await dispatch(callV2Api(getAcademyLessons(event, course_id)))
    dispatch(addLessonModels(convertLessonsData(resp)))
    return resp
  })

export const fetchAcademyLesson = (id: string, event?: 'sanofi') =>
  createThunk(async dispatch => {
    const [{ getAcademyLessonsById }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/academy'), import('@app/store/actions/api')])
    const resp = await dispatch(callV2Api(getAcademyLessonsById(id, event)))
    dispatch(addLessonModels([convertLessonData(resp)]))
    return resp
  })

export const lessonsModelsSelector = createSelector([lessonModelsSlice.selector], models => models ?? EMPTY_MODELS)

export const lessonsListSelector = createSelector([lessonsListSlice.selector], slice => slice?.data ?? EMPTY_LIST)

export const lessonsSelector = createSelector([lessonsListSelector, lessonsModelsSelector], (list, models) => list.map(id => models[id]))

export const isOnVacationSelector = createSelector([lessonsListSelector], list => list.length === 0)

export const activeCoursesSelector = createSelector([lessonsSelector], lessons =>
  uniqBy(
    lessons.map(l => l.course),
    l => l.course.id
  )
)

// The full (non-course) lessons list for the current event. With no event driver it yields an
// empty list; when the event changes the hash changes and the list re-loads. The fetched lessons
// land in the shared models map, which fetchAcademyLessons fills from the same response.
export const lessonsListResultSelector = createSelector([lessonsListSlice.selector, academyEventDriverSelector], (sliceState, driver) =>
  createLoadable(c => {
    if (!driver) return EMPTY_LIST
    return withCheckedSliceState(c, {
      sliceState,
      slice: lessonsListSlice,
      hash: driver.event,
      load: async dispatch => {
        const resp = await dispatch(fetchAcademyLessons(driver.event))
        return resp.data.map(l => l.id)
      },
    })
  })
)

const EMPTY_MODELS: { [id: string]: AcademyLesson } = {}
const EMPTY_LIST: string[] = []

const convertLessonsData = (data: ApiDataResponse<ApiAcademyLesson[]>): AcademyLesson[] => {
  const { academy_courses = {}, users = {}, avatars = {}, academy_lesson_pictures = {} } = normalize(data)
  const storeAvatars = avatars ? mapValues(avatars, a => ({ id: a.id, ...a.attributes })) : {}
  const storeUsers = users
    ? mapValues(users, user => {
        const sitter = user
        const avatars = sitter.relationships.avatars?.data ? sitter.relationships.avatars.data.map(a => storeAvatars[a.id]) : []
        return { id: sitter.id, ...sitter.attributes, avatars }
      })
    : {}

  const courses = mapValues(academy_courses, toCourseBundle)
  return data.data.map(l => ({
    id: l.id,
    ...l.attributes,
    pictures: (l.relationships.pictures?.data ?? []).map(p => academy_lesson_pictures[p.id]).filter(isTruthy),
    course: courses[l.relationships.course.data!.id],
    sitter: l.relationships.sitter.data ? storeUsers[l.relationships.sitter.data.id] : null,
  }))
}

const convertLessonData = (data: ApiDataResponse<ApiAcademyLesson>): AcademyLesson => {
  const { academy_courses = {}, users = {}, avatars = {}, academy_lesson_pictures = {} } = normalize(data)
  const storeAvatars = avatars ? mapValues(avatars, a => ({ id: a.id, ...a.attributes })) : {}
  const storeUsers = users
    ? mapValues(users, user => {
        const sitter = user
        const avatars = sitter.relationships.avatars?.data ? sitter.relationships.avatars.data.map(a => storeAvatars[a.id]) : []
        return { id: sitter.id, ...sitter.attributes, avatars }
      })
    : {}

  const courses = mapValues(academy_courses, toCourseBundle)
  return {
    id: data.data.id,
    ...data.data.attributes,
    pictures: (data.data.relationships.pictures?.data ?? []).map(p => academy_lesson_pictures[p.id]).filter(isTruthy),
    course: courses[data.data.relationships.course.data!.id],
    sitter: data.data.relationships.sitter.data ? storeUsers[data.data.relationships.sitter.data.id] : null,
  }
}
